import { Banner, Button, Checkbox, SearchBar } from '@ordr-as/umami-react'
import AddCountryDialog from 'components/AddCountryDialog'
import Chip from 'components/Chip'
import ChipGroup from 'components/Chip/ChipGroup'
import ListCard from 'components/ListCard/ListCard'
import ListCardGroup from 'components/ListCard/ListCardGroup'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import { mapCountryCodeToCountryName } from 'utils/countryUtils'
import { mapFavritAppToName } from 'utils/favritAppUtils'
import { PaymentMethodConnectionActivityStateDto, PaymentMethodCountryDto, PaymentProvider } from '../../../generated-api'

const activityStateSortOrder = [
    PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW,
    PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST,
    PaymentMethodConnectionActivityStateDto.WILL_BE_ACTIVE_IN_THE_FUTURE,
]

type PaymentMethodCountryListProps = {
    countries: PaymentMethodCountryDto[]
    paymentProvider: PaymentProvider
    paymentMethod: string
}

const PaymentMethodCountryList = (props: PaymentMethodCountryListProps) => {
    const { countries, paymentProvider, paymentMethod } = props
    const [filteredCountries, setFilteredCountries] = useState<PaymentMethodCountryDto[]>([])
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [showPastActiveCountries, setShowPastActiveCountries] = useState(false)
    const [showFutureActiveCountries, setShowFutureActiveCountries] = useState(false)
    const [addCountryDialogIsOpen, setAddCountryDialogIsOpen] = useState(false)
    const hasPastActiveCountries = useMemo(
        () => countries.some((c) => c.activityState === PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST),
        [countries]
    )
    const hasFutureActiveCountries = useMemo(
        () =>
            countries.some(
                (c) => c.activityState === PaymentMethodConnectionActivityStateDto.WILL_BE_ACTIVE_IN_THE_FUTURE
            ),
        [countries]
    )

    const navigate = useNavigate()

    const location = useLocation()
    const selectedCountryCode = location.pathname.split('/')[5]

    useEffect(() => {
        if (!countries) {
            return
        }

        const trimmedSearchQuery = searchQuery.trim()
        const searchQueryLowerCase = trimmedSearchQuery.toLowerCase()

        const filtered = countries.filter((c) => {
            if (
                !showPastActiveCountries &&
                c.activityState === PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST
            ) {
                return false
            }

            if (
                !showFutureActiveCountries &&
                c.activityState === PaymentMethodConnectionActivityStateDto.WILL_BE_ACTIVE_IN_THE_FUTURE
            ) {
                return false
            }

            return c.countryCode.toLowerCase().includes(searchQueryLowerCase)
        })

        const filteredAndSorted = filtered.sort((a, b) => {
            return activityStateSortOrder.indexOf(a.activityState) - activityStateSortOrder.indexOf(b.activityState)
        })

        setFilteredCountries(filteredAndSorted)
    }, [countries, showPastActiveCountries, showFutureActiveCountries, searchQuery])

    return (
        <div>
            <div className="mb-4 flex gap-2">
                <SearchBar
                    value={searchQuery}
                    placeholderText="Search country code"
                    aria-label="Search country code"
                    setValue={(text) => setSearchQuery(text)}
                    setActive={() => null}
                    fullWidth
                />
                <Button kind="primary" onClick={() => setAddCountryDialogIsOpen(true)}>
                    Add Country
                </Button>
            </div>

            {countries.length === 0 && (
                <Banner kind="warn" stretch="parent" onClose={() => null} className="mb-4">
                    There are no countries
                </Banner>
            )}
            <div className="flex">
                {hasPastActiveCountries && (
                    <Checkbox
                        className="mt-2 mb-3 mr-2 text-small text-grey-dark"
                        isSelected={showPastActiveCountries}
                        onChange={setShowPastActiveCountries}
                    >
                        Show past countries
                    </Checkbox>
                )}

                {hasFutureActiveCountries && (
                    <Checkbox
                        className="mt-2 mb-3 mr-2 text-small text-grey-dark"
                        isSelected={showFutureActiveCountries}
                        onChange={setShowFutureActiveCountries}
                    >
                        Show future countries
                    </Checkbox>
                )}
            </div>
            <div>
                {filteredCountries.length === 0 && (
                    <Banner kind="warn" stretch="parent" onClose={() => null}>
                        No countries match the search
                    </Banner>
                )}

                <ListCardGroup>
                    {filteredCountries.map((country, index) => (
                        <ListCard
                            key={`${country.paymentProvider}-${country.paymentMethod}-${country.countryCode}-${index}`}
                            onClick={() => navigate(`${country.countryCode}`)}
                            className={`flex ${
                                selectedCountryCode === country.countryCode.toString() && 'bg-purple-lightest'
                            }`}
                        >
                            {country.activityState === PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && (
                                <div className="flex flex-col justify-center mr-3">
                                    <div
                                        className={`rounded-full w-0 p-2 inline-block ${
                                            country.paymentsEnabled ? 'bg-green-dark' : 'bg-orange'
                                        }`}
                                        data-tip
                                        data-for={`country-payments-enabled-tooltip-${country.countryCode}`}
                                    />
                                    <ReactTooltip
                                        id={`country-payments-enabled-tooltip-${country.countryCode}`}
                                        arrowColor="transparent"
                                        effect="solid"
                                        padding="1px 4px"
                                        className="text-bold !opacity-100"
                                    >
                                        Payments are {country.paymentsEnabled ? 'enabled' : 'disabled'}
                                    </ReactTooltip>
                                </div>
                            )}

                            <div className="flex-1 flex flex-col justify-center">
                                <h4
                                    className={`${
                                        country.activityState !==
                                            PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && 'text-grey-dark'
                                    }`}
                                >
                                    {mapCountryCodeToCountryName(country.countryCode)} ({country.countryCode})
                                </h4>
                            </div>

                            {country.activityState === PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && (
                                <ChipGroup
                                    heading="Enabled in"
                                    showEmptyPlaceholder={country.defaultEnabledInApps.size === 0}
                                >
                                    {Array.from(country.defaultEnabledInApps).map((app) => (
                                        <Chip
                                            key={`${country.countryCode}-${app}`}
                                            size="small"
                                            text={mapFavritAppToName(app)}
                                        />
                                    ))}
                                </ChipGroup>
                            )}

                            {country.activityState !== PaymentMethodConnectionActivityStateDto.IS_ACTIVE_NOW && (
                                <p className="text-small text-grey-dark">
                                    Country has no active configuration, but
                                    {country.activityState ===
                                    PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST
                                        ? ' did '
                                        : ' does '}
                                    have one in the
                                    {country.activityState ===
                                    PaymentMethodConnectionActivityStateDto.WAS_ACTIVE_IN_THE_PAST
                                        ? ' past'
                                        : ' future'}
                                    .
                                </p>
                            )}
                        </ListCard>
                    ))}
                </ListCardGroup>
            </div>

            <AddCountryDialog
                paymentProvider={paymentProvider}
                paymentMethod={paymentMethod}
                dialogIsOpen={addCountryDialogIsOpen}
                onClose={() => setAddCountryDialogIsOpen(false)}
            />
        </div>
    )
}

export default PaymentMethodCountryList
